/**
 * ProductHeroVisual — animated SVG hero visuals per AI Stack page.
 * One component per slug, each modelled on the user-supplied reference image:
 *   contextualpulse → bubble-network classification graph
 *   signalsense     → tweet card + "AI Intent Analysis" panel
 *   agentiq         → phone-frame analytics with animated bars
 *   responsegenie   → chat bubble + AI-generated response card
 *
 * Everything is pure CSS + SVG (no images, no extra deps). Animations are
 * keyed in globals.css under .lb-hv-* utility classes. The shell .lb-hv
 * keeps the parent's gradient orb backdrop and just swaps the contents.
 */
export default function ProductHeroVisual({ slug }) {
  switch (slug) {
    case 'contextualpulse': return <ContextualPulseVisual />;
    case 'signalsense':     return <SignalSenseVisual />;
    case 'agentiq':         return <AgentIqVisual />;
    case 'responsegenie':   return <ResponseGenieVisual />;
    default:                return null;
  }
}

/* ── ContextualPulse — bubble-network classifier ────────────────────
   Big central "Complaint" node, satellite bubbles for other categories,
   thin connecting lines with subtle stroke-dash animation. */
function ContextualPulseVisual() {
  return (
    <div className="lb-hv lb-hv-network" aria-hidden="true">
      <svg viewBox="0 0 400 320" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="cp-red" cx="35%" cy="35%">
            <stop offset="0%" stopColor="#FF6B6B"/><stop offset="100%" stopColor="#D32F2F"/>
          </radialGradient>
          <radialGradient id="cp-green" cx="35%" cy="35%">
            <stop offset="0%" stopColor="#7DD87D"/><stop offset="100%" stopColor="#2E8B2E"/>
          </radialGradient>
          <radialGradient id="cp-yellow" cx="35%" cy="35%">
            <stop offset="0%" stopColor="#FFE066"/><stop offset="100%" stopColor="#F0B000"/>
          </radialGradient>
          <radialGradient id="cp-blue" cx="35%" cy="35%">
            <stop offset="0%" stopColor="#7DA9FF"/><stop offset="100%" stopColor="#0098FF"/>
          </radialGradient>
        </defs>

        {/* Connecting lines (animated dash) */}
        <g className="lb-hv-lines" stroke="rgba(0,152,255,0.35)" strokeWidth="1" fill="none" strokeDasharray="3 5">
          <line x1="260" y1="200" x2="160" y2="80"/>
          <line x1="260" y1="200" x2="340" y2="100"/>
          <line x1="260" y1="200" x2="90"  y2="160"/>
          <line x1="260" y1="200" x2="340" y2="240"/>
          <line x1="260" y1="200" x2="170" y2="260"/>
          <line x1="260" y1="200" x2="80"  y2="240"/>
        </g>

        {/* Central node — Complaint */}
        <g className="lb-hv-pulse" style={{ transformOrigin: '260px 200px' }}>
          <circle cx="260" cy="200" r="58" fill="url(#cp-red)" opacity="0.25"/>
          <circle cx="260" cy="200" r="48" fill="url(#cp-red)"/>
          <text x="260" y="195" textAnchor="middle" fill="#fff" fontWeight="700" fontSize="20">830</text>
          <text x="260" y="215" textAnchor="middle" fill="#fff" fontWeight="500" fontSize="10">Complaint</text>
        </g>

        {/* Satellite nodes */}
        <Bubble x={160} y={80}  r={34} g="cp-green"  num="441" label="Appreciation" delay="0s"/>
        <Bubble x={340} y={100} r={26} g="cp-red"    num="45"  label="Issue"        delay="0.4s"/>
        <Bubble x={90}  y={160} r={20} g="cp-yellow" num="12"  label="Query"        delay="0.8s"/>
        <Bubble x={340} y={240} r={22} g="cp-yellow" num="87"  label="Service"      delay="0.2s"/>
        <Bubble x={170} y={260} r={28} g="cp-green"  num="187" label="Feedback"     delay="0.6s"/>
        <Bubble x={80}  y={240} r={18} g="cp-blue"   num="5"   label="Request"      delay="1s"/>
      </svg>
    </div>
  );
}

function Bubble({ x, y, r, g, num, label, delay }) {
  return (
    <g className="lb-hv-float" style={{ animationDelay: delay, transformOrigin: `${x}px ${y}px` }}>
      <circle cx={x} cy={y} r={r}     fill={`url(#${g})`}/>
      <text x={x} y={y - 1} textAnchor="middle" fill="#fff" fontWeight="700" fontSize="11">{num}</text>
      <text x={x} y={y + r + 12} textAnchor="middle" fill="#3a4677" fontWeight="500" fontSize="9">{label}</text>
    </g>
  );
}

/* ── SignalSense — tweet card + AI Intent Analysis panel ────────────
   Two stacked cards, the AI panel slides up over the tweet card. */
function SignalSenseVisual() {
  return (
    <div className="lb-hv lb-hv-tweet" aria-hidden="true">
      {/* Tweet card */}
      <div className="lb-hv-tweet__card">
        <div className="lb-hv-tweet__head">
          <span className="lb-hv-tweet__avatar"/>
          <span className="lb-hv-tweet__name-stub"/>
          <svg className="lb-hv-tweet__verified" viewBox="0 0 24 24" width="14" height="14" fill="#0098FF">
            <path d="M22.5 12.5l-2-2.5 1-3-2.8-1.4-1-3L14.5 3 12 1l-2.5 2-3.2.6-1 3L2.5 8l1 3-2 2.5 2 2.5-1 3 2.8 1.4 1 3L9.5 21l2.5 2 2.5-2 3.2-.6 1-3 2.8-1.4-1-3z"/>
            <path d="M16 9l-5.5 5.5L8 12" stroke="#fff" strokeWidth="2" fill="none"/>
          </svg>
          <span className="lb-hv-tweet__followers">10K Followers</span>
        </div>
        <p className="lb-hv-tweet__body">
          <b>LuxeStay's 'world-class' culinary experience:</b> cold food, overpriced, and served with a side of attitude. Michelin stars must be on vacation!
          <span className="lb-hv-tweet__tag"> #LuxeStayDining #Overrated</span>
        </p>
      </div>

      {/* AI Intent Analysis panel — slides in */}
      <div className="lb-hv-ai-card">
        <div className="lb-hv-ai-card__head">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2">
            <path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z"/>
          </svg>
          AI Intent Analysis
        </div>
        <div className="lb-hv-ai-card__body">
          <div className="lb-hv-ai-card__alert">SignalSense Alert: Reputation Risk</div>
          <div className="lb-hv-ai-card__cols">
            <div>
              <div className="lb-hv-ai-card__col-title">Custom Attributes</div>
              <p><b>Service Area:</b> Restaurant</p>
              <p><b>Complaint Type:</b> Food Quality</p>
              <p><b>Reviewer Type:</b> Food Blogger</p>
            </div>
            <div>
              <div className="lb-hv-ai-card__col-title">Extended Attributes</div>
              <p><b>Sarcasm:</b> True</p>
              <p><b>Visibility:</b> High</p>
              <p><b>Reputation:</b> Medium</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── AgentIQ — phone-frame analytics dashboard ──────────────────────
   Phone outline with chart bars animating heights on load. */
function AgentIqVisual() {
  const bars = [38, 50, 62, 28, 45, 70, 92];
  return (
    <div className="lb-hv lb-hv-phone" aria-hidden="true">
      <div className="lb-hv-phone__frame">
        <div className="lb-hv-phone__notch"/>
        <div className="lb-hv-phone__screen">
          <div className="lb-hv-phone__head">
            <span style={{ fontWeight: 700, fontSize: 12, color: '#0a1230' }}>Quality Scores</span>
            <div className="lb-hv-phone__tabs">
              <span className="lb-hv-phone__tab is-on">Weekly</span>
              <span className="lb-hv-phone__tab">Monthly</span>
            </div>
          </div>
          <div className="lb-hv-phone__metric">
            <span className="lb-hv-phone__metric-num">94.2%</span>
            <span className="lb-hv-phone__metric-lbl">Avg CSAT this week</span>
          </div>
          <div className="lb-hv-phone__chart">
            {bars.map((h, i) => (
              <span
                key={i}
                className="lb-hv-phone__bar"
                style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }}
              />
            ))}
          </div>
          <div className="lb-hv-phone__legend">
            <span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span>
          </div>
        </div>
      </div>

      <div className="lb-hv-phone__chip lb-hv-phone__chip--top">
        <div className="lb-hv-phone__chip-dot lb-hv-phone__chip-dot--green"/>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0a1230' }}>Live coaching</div>
          <div style={{ fontSize: 10, color: '#3a4677' }}>+22% lift this month</div>
        </div>
      </div>
      <div className="lb-hv-phone__chip lb-hv-phone__chip--bottom">
        <div className="lb-hv-phone__chip-dot lb-hv-phone__chip-dot--blue"/>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0a1230' }}>Auto-QA</div>
          <div style={{ fontSize: 10, color: '#3a4677' }}>100% of tickets scored</div>
        </div>
      </div>
    </div>
  );
}

/* ── ResponseGenie — chat + AI-generated response card ──────────────
   Customer message + "Generate Response" button, then AI reply slides in. */
function ResponseGenieVisual() {
  return (
    <div className="lb-hv lb-hv-chat" aria-hidden="true">
      {/* User avatar bubble */}
      <div className="lb-hv-chat__avatar">
        <svg viewBox="0 0 64 64" width="64" height="64">
          <circle cx="32" cy="24" r="12" fill="#7df3ff" opacity="0.4"/>
          <path d="M12 56c0-11 9-20 20-20s20 9 20 20" fill="#7df3ff" opacity="0.4"/>
        </svg>
      </div>

      {/* Customer message */}
      <div className="lb-hv-chat__msg">
        Hi, I need to reschedule my flight from New York to London. Can you help me with that? My booking ref is ABC123.
      </div>

      <div className="lb-hv-chat__btn">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="#fff" style={{ marginRight: 6 }}>
          <path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z"/>
        </svg>
        Generate Response
      </div>

      {/* Generated AI response */}
      <div className="lb-hv-chat__response">
        <div className="lb-hv-chat__response-head">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="#0098FF">
            <path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z"/>
          </svg>
          Generate Response
        </div>
        <p>I'd be happy to help you reschedule your flight. I see your booking ABC123 from NYC to London is on July 15th. Per policy, there's a $100 rescheduling fee. When would you like to travel instead?</p>
        <div className="lb-hv-chat__response-actions">
          <span className="lb-hv-chat__source">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="#0a8a4a"><path d="M20 6l-9 13-5-5 1.5-1.5L11 16l8-12z"/></svg>
            Response from Knowledge base
          </span>
          <span className="lb-hv-chat__use">Use</span>
        </div>
      </div>

      {/* Sparkles */}
      <svg className="lb-hv-chat__spark lb-hv-chat__spark--a" viewBox="0 0 24 24" width="22" height="22" fill="#FFB800">
        <path d="M12 2l2 7 7 2-7 2-2 7-2-7-7-2 7-2 2-7z"/>
      </svg>
      <svg className="lb-hv-chat__spark lb-hv-chat__spark--b" viewBox="0 0 24 24" width="14" height="14" fill="#FFB800">
        <path d="M12 2l2 7 7 2-7 2-2 7-2-7-7-2 7-2 2-7z"/>
      </svg>
    </div>
  );
}
